(() => {
  'use strict';
  const controllers = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const stackedPanels = window.matchMedia('not all and (min-width: 1000px)');

  const initialize = (sidebar) => {
    if (controllers.has(sidebar)) return;
    const main = sidebar.querySelector('[slot="main-panel"]');
    const panel = sidebar.querySelector('[slot="collapsible-panel"]');
    if (!main) return;
    const abort = new AbortController();
    const signal = abort.signal;
    const originalReveal = sidebar.revealItems;
    const originalEnter = sidebar.createEnterAnimationControls;
    const originalLeave = sidebar.createLeaveAnimationControls;
    const originalShow = sidebar.show;
    const originalHide = sidebar.hide;
    let frame = 0;
    let activeId = null;
    let opener = null;
    let focusedPanel = null;
    let handoffPending = false;
    let handoffGeneration = 0;
    let destroyed = false;
    let openingControls = null;
    let pendingHide = null;
    let leaveStart = null;
    const drawerAnimations = new Set();
    const cancelDrawer = () => {
      drawerAnimations.forEach((animation) => animation.cancel());
      drawerAnimations.clear();
    };

    const showMain = () => {
      main.getAnimations().forEach((animation) => animation.cancel());
      main.style.opacity = '1';
      main.style.transform = 'translateX(0)';
    };

    if (typeof originalReveal === 'function') {
      sidebar.revealItems = function () {
        if (destroyed || !this.open || !this.isConnected) return { finished: Promise.resolve(), finish() {} };
        showMain();
        return { finished: Promise.resolve(), finish() {} };
      };
    }

    const animateDrawer = (entering) => {
      const content = sidebar.getShadowPartByName('content');
      const overlay = sidebar.getShadowPartByName('overlay');
      if (!content || !overlay || typeof content.animate !== 'function') {
        return (entering ? originalEnter : originalLeave).call(sidebar);
      }
      const interrupted = drawerAnimations.size > 0;
      const currentTransform = !entering && leaveStart ? leaveStart.transform : window.getComputedStyle(content).transform;
      const currentOpacity = !entering && leaveStart ? leaveStart.opacity : window.getComputedStyle(overlay).opacity;
      const continued = interrupted || (!entering && Boolean(leaveStart));
      leaveStart = null;
      cancelDrawer();
      if (entering) showMain();
      content.style.marginInlineStart = sidebar.openFrom === 'right' ? 'auto' : '0';
      const outside = `translateX(calc(var(--transform-logical-flip) * ${sidebar.openFrom === 'right' ? '100%' : '-100%'}))`;
      const endTransform = entering ? 'translateX(0)' : outside;
      const endOpacity = entering ? '1' : '0';
      content.style.transform = endTransform;
      overlay.style.opacity = endOpacity;
      const fullDuration = entering ? 520 : 400;
      const remaining = continued ? entering ? 1 - Number(currentOpacity) : Number(currentOpacity) : 1;
      const duration = reducedMotion.matches ? 1 : fullDuration * Math.max(.25, Math.min(1, Number.isFinite(remaining) ? remaining : 1));
      const options = { duration, easing: 'cubic-bezier(.7,0,.2,1)', fill: 'both' };
      const animations = [
        content.animate({ transform: [continued ? currentTransform : entering ? outside : 'translateX(0)', endTransform] }, options),
        overlay.animate({ opacity: [continued ? currentOpacity : entering ? '0' : '1', endOpacity] }, options)
      ];
      animations.forEach((animation) => drawerAnimations.add(animation));
      const controls = {
        finished: Promise.all(animations.map((animation) => animation.finished.catch(() => {}).then(() => {
          drawerAnimations.delete(animation);
          animation.cancel();
        }))),
        finish() {
          animations.forEach((animation) => {
            if (animation.playState === 'running' || animation.playState === 'pending') animation.finish();
          });
        }
      };
      if (entering) {
        openingControls = controls;
        controls.finished.then(() => {
          if (openingControls === controls) openingControls = null;
        });
      }
      if (reducedMotion.matches) controls.finish();
      return controls;
    };
    sidebar.createEnterAnimationControls = () => animateDrawer(true);
    sidebar.createLeaveAnimationControls = () => animateDrawer(false);
    sidebar.show = function (animate = true) {
      handoffGeneration += 1;
      if (pendingHide) return pendingHide.then(() => destroyed ? undefined : originalShow.call(this, animate));
      return originalShow.call(this, animate);
    };
    sidebar.hide = function () {
      if (pendingHide) return pendingHide;
      if (!this.open) return originalHide.call(this);
      if (openingControls) {
        const content = this.getShadowPartByName('content');
        const overlay = this.getShadowPartByName('overlay');
        leaveStart = { transform: window.getComputedStyle(content).transform, opacity: window.getComputedStyle(overlay).opacity };
        const opening = openingControls;
        opening.finish();
        pendingHide = opening.finished.then(() => destroyed ? undefined : originalHide.call(this));
      } else {
        pendingHide = originalHide.call(this);
      }
      const currentHide = pendingHide;
      currentHide.then(() => {
        if (pendingHide === currentHide) pendingHide = null;
      }, () => {
        if (pendingHide === currentHide) pendingHide = null;
      });
      return currentHide;
    };
    const finishMotion = () => {
      if (!reducedMotion.matches) return;
      const animations = new Set(sidebar.getAnimations({ subtree: true }));
      sidebar.shadowRoot?.querySelectorAll('*').forEach((element) => element.getAnimations().forEach((animation) => animations.add(animation)));
      animations.forEach((animation) => {
        if (animation.playState === 'running' || animation.playState === 'pending') {
          try { animation.finish(); } catch (_) { /* An interrupted theme animation may already be idle. */ }
        }
      });
    };
    const sync = () => {
      frame = 0;
      if (!sidebar.isConnected) return;
      const nextId = sidebar.open ? panel?.getAttribute('aria-activedescendant') || null : null;
      const stacked = stackedPanels.matches;
      main.inert = Boolean(nextId && stacked);
      if (nextId !== activeId) {
        activeId = nextId;
        focusedPanel = null;
        if (!nextId) sidebar.querySelectorAll('.header-sidebar__main-panel .header-sidebar__linklist [aria-expanded="true"]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      }
      if (nextId && panel?.open) {
        const current = document.getElementById(nextId);
        if (current && !current.hidden) {
          const heading = current.querySelector('.mn-nav-category-title');
          if (heading) panel.setAttribute('aria-labelledby', heading.id);
          if (stacked && focusedPanel !== nextId) {
            current.querySelector('[data-action="close-panel"]')?.focus({ preventScroll: true });
            focusedPanel = nextId;
          }
        }
      } else if (!nextId && sidebar.open && opener && (!panel || !panel.open)) {
        opener.focus({ preventScroll: true });
        opener = null;
      }
      finishMotion();
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(sync); };
    sidebar.addEventListener('click', (event) => {
      const target = event.target.closest('a, button');
      if (!target) return;
      if (target.closest('.header-sidebar__main-panel .header-sidebar__linklist') && target.hasAttribute('aria-controls')) opener = target;
      const dialogId = target.getAttribute('data-mn-nav-dialog');
      const dialog = dialogId ? document.getElementById(dialogId) : null;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;
      if (!dialog || typeof dialog.show !== 'function') {
        const href = target.getAttribute('href');
        if (target.tagName === 'A' && href && !href.startsWith('#') && !target.hasAttribute('download') && (!target.target || target.target === '_self')) {
          try {
            const destination = new URL(href, document.baseURI);
            if (destination.origin === window.location.origin && destination.href !== window.location.href) sidebar.hide().catch(() => {});
          } catch {}
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (handoffPending) return;
      handoffPending = true;
      const currentHandoff = ++handoffGeneration;
      const handoff = async () => {
        try {
          await sidebar.hide();
          if (handoffGeneration === currentHandoff && !sidebar.open && !destroyed && sidebar.isConnected && dialog.isConnected) await dialog.show();
        } finally {
          handoffPending = false;
        }
      };
      // A theme-editor replacement can interrupt the destination dialog's animation.
      handoff().catch(() => {});
    }, { capture: true, signal });
    ['dialog:before-show', 'dialog:after-show', 'dialog:before-hide', 'dialog:after-hide'].forEach((name) => sidebar.addEventListener(name, schedule, { signal }));
    // Escape uses the original nested focus trap; clear its active panel as well.
    panel?.addEventListener('dialog:before-hide', () => {
      if (!panel.hasAttribute('aria-activedescendant')) return;
      panel.removeAttribute('aria-activedescendant');
      if (sidebar.open) sidebar.revealItems?.();
      schedule();
    }, { signal });
    const observer = new MutationObserver(schedule);
    observer.observe(sidebar, { subtree: true, attributes: true, attributeFilter: ['open', 'hidden', 'aria-activedescendant'] });
    const cleanup = () => {
      destroyed = true;
      handoffGeneration += 1;
      handoffPending = false;
      cancelDrawer();
      abort.abort();
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      if (originalReveal) sidebar.revealItems = originalReveal;
      sidebar.createEnterAnimationControls = originalEnter;
      sidebar.createLeaveAnimationControls = originalLeave;
      sidebar.show = originalShow;
      sidebar.hide = originalHide;
      leaveStart = null;
      main.inert = false;
      controllers.delete(sidebar);
    };
    controllers.set(sidebar, { schedule, cleanup });
    schedule();
  };
  const scan = () => document.querySelectorAll('.mn-header-sidebar').forEach(initialize);
  const update = () => document.querySelectorAll('.mn-header-sidebar').forEach((sidebar) => controllers.get(sidebar)?.schedule());
  Promise.all([customElements.whenDefined('header-sidebar'), customElements.whenDefined('header-sidebar-collapsible-panel')]).then(scan);
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', (event) => {
    document.querySelectorAll('.mn-header-sidebar').forEach((sidebar) => {
      if (sidebar.dataset.mnNavigationSection === String(event.detail?.sectionId)) controllers.get(sidebar)?.cleanup();
    });
  });
  reducedMotion.addEventListener('change', update);
  stackedPanels.addEventListener('change', update);
})();
